import * as React from "react";
import { useState } from "react";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

interface UniversalDateRangePickerProps {
    date: { from: Date; to: Date };
    onDateChange: (date: { from: Date; to: Date }) => void;
    className?: string;
}

export function UniversalDateRangePicker({
    date,
    onDateChange,
    className,
}: UniversalDateRangePickerProps) {
    const [open, setOpen] = useState(false);

    // Range selection state
    const [selecting, setSelecting] = useState<'start' | 'end'>('start');
    const [tempFrom, setTempFrom] = useState<Date | null>(null);
    const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

    // Format display text
    const formatDisplay = () => {
        if (!date?.from || !date?.to) return "Select date range";
        const from = date.from;
        const to = date.to;

        if (from.getTime() === to.getTime()) {
            return format(from, "MMM d, yyyy");
        }
        if (from.getFullYear() === to.getFullYear()) {
            return `${format(from, "MMM d")} - ${format(to, "MMM d, yyyy")}`;
        }
        return `${format(from, "MMM d, yyyy")} - ${format(to, "MMM d, yyyy")}`;
    };

    // Handle day click - snaps to full weeks (Monday start)
    const handleDayClick = (day: Date) => {
        const weekStart = startOfWeek(day, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(day, { weekStartsOn: 1 });

        if (selecting === 'start') {
            setTempFrom(weekStart);
            setSelecting('end');
            setHoveredDate(null);
        } else {
            const fromDate = tempFrom || date.from;

            if (weekEnd < fromDate) {
                // User selected earlier week, swap
                onDateChange({ from: weekStart, to: endOfWeek(fromDate, { weekStartsOn: 1 }) });
            } else {
                onDateChange({ from: fromDate, to: weekEnd });
            }

            setTempFrom(null);
            setSelecting('start');
            setHoveredDate(null);
            setOpen(false);
        }
    };

    // Calculate dynamic range for visualization
    const getSelectedRange = () => {
        if (selecting === 'end' && tempFrom) {
            if (hoveredDate) {
                const hoveredEnd = endOfWeek(hoveredDate, { weekStartsOn: 1 });
                if (hoveredEnd < tempFrom) {
                    return { from: startOfWeek(hoveredDate, { weekStartsOn: 1 }), to: endOfWeek(tempFrom, { weekStartsOn: 1 }) };
                }
                return { from: tempFrom, to: hoveredEnd };
            }
            return { from: tempFrom, to: endOfWeek(tempFrom, { weekStartsOn: 1 }) };
        }
        return date;
    };

    // Reset state on open
    const handleOpenChange = (isOpen: boolean) => {
        if (isOpen) {
            setSelecting('start');
            setTempFrom(null);
            setHoveredDate(null);
        }
        setOpen(isOpen);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        "justify-start text-left font-normal",
                        !date && "text-muted-foreground",
                        "min-w-64",
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formatDisplay()}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 space-y-4">
                    <div className="text-sm text-muted-foreground text-center animate-fade-in">
                        {selecting === 'start' ? 'Pick start week' : 'Pick end week'}
                    </div>

                    <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                        <Calendar
                            initialFocus
                            mode="range"
                            defaultMonth={new Date()}
                            selected={getSelectedRange()}
                            onDayClick={handleDayClick}
                            onDayMouseEnter={setHoveredDate}
                            onSelect={() => { }}
                            numberOfMonths={1}
                            weekStartsOn={1}
                            showWeekNumber
                            classNames={{
                                head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                                row: "flex w-full mt-2 hover:bg-muted/50 rounded-md transition-colors cursor-pointer",
                                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-transparent",
                                day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                                day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                            }}
                        />
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
